import React from "react";

import { StatChart } from "~/components/stat-chart";
import { autoValueFontSize } from "~/components/stat-chart/utils";
import { Metric } from "~/models/metric";
import type { ChartThresholdStep } from "~/tools/chart-thresholds";
import { toFormatterUnit } from "~/tools/formatter-unit";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { ChartsGrid, type ChartsGridProps } from "../charts-grid";
import { DEFAULT_STEP as DEFAULT_THRESHOLDS_STEP } from "../thresholds/settings";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "./settings";

export interface StatGridProps
	extends Settings,
		Pick<ChartsGridProps, "className" | "width" | "height" | "minCellSize" | "timeSeries" | "onShowAll"> {
	timeRange: TimeRange;
}

export const StatGrid = React.forwardRef<React.ComponentRef<typeof ChartsGrid>, StatGridProps>(
	({ className, width, height, minCellSize, timeRange, timeSeries, onShowAll, ...settings }, ref) => {
		const unit = toFormatterUnit(settings.unit, DEFAULT_SETTINGS.unit);
		const decimalPlaces = settings.decimals ?? DEFAULT_SETTINGS.decimals;
		const calculation = settings.calculation ?? DEFAULT_SETTINGS.calculation;
		const sparkline = settings.sparkline ?? DEFAULT_SETTINGS.sparkline;
		const thresholdDefaultColor = settings.color ?? DEFAULT_SETTINGS.color;
		const thresholdSteps = (settings.thresholds ?? DEFAULT_SETTINGS.thresholds).map(
			(step): ChartThresholdStep => ({
				value: step.value ?? DEFAULT_THRESHOLDS_STEP.value,
				color: step.color ?? DEFAULT_THRESHOLDS_STEP.color,
			}),
		);

		const interval = React.useMemo(() => timeRange.toInterval(), [timeRange]);

		return (
			<ChartsGrid
				autoValueFontSize={autoValueFontSize}
				calculation={calculation}
				className={className}
				decimalPlaces={decimalPlaces}
				height={height}
				minCellSize={minCellSize}
				onShowAll={onShowAll}
				ref={ref}
				timeSeries={timeSeries}
				unit={unit}
				width={width}
			>
				{({ metric, values, alias }, cell) => (
					<StatChart
						calculation={calculation}
						className="size-full"
						decimalPlaces={decimalPlaces}
						fontSize={settings.fontSize ?? cell.fontSize}
						height={cell.height}
						interval={interval}
						name={Metric.toLabel(alias, metric)}
						points={values}
						thresholds={[thresholdDefaultColor, ...thresholdSteps]}
						unit={unit}
						width={cell.width}
						withArea={sparkline}
					/>
				)}
			</ChartsGrid>
		);
	},
);
